// Floating notification bell — top-right of the screen, does not affect any card.
import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, AlertCircle, Clock, Receipt } from "lucide-react";
import { useInstallments } from "@/lib/useInstallmentsCache";
import { useCheques } from "@/lib/useCheques";
import { useI18n } from "@/context/I18nContext";
import { upcomingReminders } from "@/lib/chequeDueCalc";
import { pickReminder } from "@/lib/politeReminders";

const daysBetween = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
};

export function NotificationBell() {
  const { t, lang } = useI18n();
  const inst = useInstallments();
  const chq = useCheques();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const items = useMemo(() => {
    const rows = [];
    for (const i of inst.active || []) {
      const dd = daysBetween(i.next_due);
      if (i.is_overdue) rows.push({ id: `i-${i.id}`, kind: "overdue-inst", name: i.name, label: t("overdue"), days: dd });
      else if (dd !== null && dd <= 7) rows.push({ id: `i-${i.id}`, kind: "soon-inst", name: i.name, label: `${t("dueIn")} ${dd} ${t("days")}`, days: dd });
    }
    for (const c of upcomingReminders(chq.items || [])) {
      rows.push({ id: `c-${c.id}`, kind: c.sev === "overdue" ? "overdue-chq" : "soon-chq",
        name: c.payee, label: pickReminder({ lang, days: c.days, amount: c.amount, ccy: c.currency, payee: c.payee }), days: c.days });
    }
    return rows.sort((a, b) => (a.days ?? 99) - (b.days ?? 99));
  }, [inst.active, chq.items, t, lang]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const count = items.length;
  return (
    <div ref={ref} className="absolute z-30" style={{ top: "-28px", insetInlineEnd: "-8px" }}>
      <button data-testid="notif-bell-btn" onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 rounded-full grid place-items-center active:scale-95 transition-transform"
        style={{
          background: "linear-gradient(135deg, #f4f6f9 0%, #eceef1 55%, #dfe2e7 100%)",
          boxShadow: "0 6px 14px rgba(30,58,138,0.18), 0 1px 2px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,0.9), inset 0 -1px 2px rgba(200,202,208,0.6)",
          border: "1px solid rgba(245,184,0,0.45)",
        }}>
        <Bell className="w-[18px] h-[18px]" strokeWidth={2.5} style={{ color: "#1e3a8a" }} fill={count > 0 ? "#f5b800" : "transparent"} />
        {count > 0 && (
          <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold grid place-items-center ring-2 ring-[#eceef1]"
            style={{ background: "#f5b800", color: "#1e3a8a" }}>
            {count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel" className="absolute top-[48px] end-0 w-80 neu p-3 space-y-2 max-h-[70vh] overflow-y-auto">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 px-1">{t("notifications")}</p>
          {items.length === 0 ? (
            <p data-testid="notif-empty" className="text-sm text-slate-500 py-6 text-center">{t("noNotifications")}</p>
          ) : items.map((it) => (
            <div key={it.id} data-testid={`notif-item-${it.id}`} className="p-2.5 rounded-xl neu-sm flex items-start gap-2.5">
              <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${it.kind.startsWith("overdue") ? "text-warn" : "text-primary"} bg-secondary`}>
                {it.kind.endsWith("chq") ? <Receipt className="w-4 h-4" /> : it.kind.startsWith("overdue") ? <AlertCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{it.name}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{it.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
